import { useCallback } from "react";

import type * as FormModelType from "@/components/features/cancellation/model/type";
import * as Shared from "@/components/features/cancellation/ui/_shared";

import type { FormOptionType } from "@/components/ui/form/_types/type";

import type { CheckedState } from "@radix-ui/react-checkbox";

export const useActions = (
	viewModel: FormModelType.CancellationFormModel["ViewModel"],

	onSetViewModel: (viewModel: FormModelType.CancellationFormModel["ViewModel"]) => void,
	onCancellation: () => Promise<boolean>,
) => {
	const handleChangeReasonOfChurnInfo = useCallback(
		(checked: CheckedState, item: FormOptionType) => {
			const convertedArrayReason = Shared.Converter.fromReasonToArrayConverter(viewModel.churnInfo.reason.value);
			const newReasonValue = checked
				? convertedArrayReason.concat(item.value).join(", ")
				: convertedArrayReason.filter((value) => value !== item.value).join(", ");

			// 「他の予約システムへ乗り換え」のチェックを外した場合、製品名と理由をリセット
			const shouldResetAlternativeSystem = !checked && item.value === "他の予約システムへ乗り換え";

			onSetViewModel({
				...viewModel,
				churnInfo: {
					...viewModel.churnInfo,
					reason: {
						value: newReasonValue,
						error: undefined,
						isEdited: false,
					},
					...(shouldResetAlternativeSystem && {
						alternativeSystemProductName: {
							value: null,
							error: undefined,
							isEdited: false,
						},
						alternativeSystemReason: {
							value: null,
							error: undefined,
							isEdited: false,
						},
					}),
				},
			});
		},
		[viewModel, onSetViewModel],
	);

	const handleChangeCommentOfChurnInfo = useCallback(
		(value: string) => {
			onSetViewModel({
				...viewModel,
				churnInfo: {
					...viewModel.churnInfo,
					comment: {
						value,
						error: undefined,
						isEdited: false,
					},
				},
			});
		},
		[viewModel, onSetViewModel],
	);

	const handleChangeAlternativeSystemProductName = useCallback(
		(value: string) => {
			onSetViewModel({
				...viewModel,
				churnInfo: {
					...viewModel.churnInfo,
					alternativeSystemProductName: {
						value,
						error: undefined,
						isEdited: false,
					},
				},
			});
		},
		[viewModel, onSetViewModel],
	);

	const handleChangeAlternativeSystemReason = useCallback(
		(value: string) => {
			onSetViewModel({
				...viewModel,
				churnInfo: {
					...viewModel.churnInfo,
					alternativeSystemReason: {
						value,
						error: undefined,
						isEdited: false,
					},
				},
			});
		},
		[viewModel, onSetViewModel],
	);

	/**
	 *
	 * @todo Notify などの副作用を追加
	 */
	const handleClickCancellation = useCallback(async () => {
		await onCancellation();
	}, [onCancellation]);

	return {
		handleChangeReasonOfChurnInfo,
		handleChangeCommentOfChurnInfo,
		handleChangeAlternativeSystemProductName,
		handleChangeAlternativeSystemReason,
		handleClickCancellation,
	};
};
